<script setup lang="ts">
import type { AgentOnPreview } from '@@/shared/agent/types';
import type { PropertyOnPage } from '@@/shared/property/api-types';
import type { ContactForm } from '@@/shared/contact/types';
import { TRANSACTION_TYPE } from '@@/shared/property/types';

const props = withDefaults(defineProps<{
  agent?: AgentOnPreview
  property?: PropertyOnPage
  variant?: `default` | `property`
}>(), {
  variant: 'default',
});

const { notifySuccess, notifyError } = useNotifications();

const open = defineModel<boolean>({ default: false });

const { t } = useI18n();
const agentUserName = toRef(() => props.agent?.userName ?? ``);
const title = computed(() => {
  if (props.variant === 'default') return t(`agent.form.title`);
  if (props.agent?.displayGender === `female`) return t(`property.contact_agent.title.female`);
  return t(`property.contact_agent.title.male`);
});

const propertyListingRef = computed(() => props.property?.propertyListingRef ?? '');
const { data: contactFormResponse, isPending: loadingContactForm, error: errorContactForm, mutate: mutateContactForm } = useAgentContactForm(agentUserName);
const { data: propertyFormResponse, isPending: loadingPropertyForm, error: errorPropertyForm, mutate: mutatePropertyForm } = usePropertyContactForm(propertyListingRef);
const loading = computed(() => loadingContactForm.value || loadingPropertyForm.value);

function submitForm(form: ContactForm) {
  if (props.property) {
    mutatePropertyForm(form);
    trackEvent({
      event: 'generate_lead',
      lead_category: props.property.transactionType === TRANSACTION_TYPE.RENTAL ? 'rent' : 'buyer',
      agent_id: props.property.agent.localId,
      property_id: props.property.propertyListingRef,
      property_category: getTrackingPropertyCategory(props.property),
    });
  }
  else {
    trackEvent({
      event: 'generate_lead',
      lead_category: 'buyer',
      lead_type: 'contact_by_phone',
      agent_id: props.agent?.localId,
      property_id: propertyListingRef.value,
      property_category: undefined, // Assuming property category is not needed here
    });
    mutateContactForm(form);
  }
}

watch([contactFormResponse, propertyFormResponse], (response) => {
  if (!response) return;
  open.value = false;
  notifySuccess(t('core.form.message_sent'));
});

watch([errorContactForm, errorPropertyForm], (error) => {
  if (!error) return;
  notifyError(t('core.error.notification'));
});
</script>

<template>
  <IadDialog
    v-model="open"
    modal-name="contact_agent"
  >
    <template #top-content>
      <IadTitle
        :text="title"
        variant="medium"
        tag="p"
      />
    </template>
    <div class="w-screen lg:max-w-3xl mx-auto lg:max-w-md px-m text-sm font-semibold flex flex-col gap-2xs">
      <span v-text="$t('agent.form.description') " />
      <AgentTinyPreview
        v-if="agent"
        :agent
        class="max-w-md items-center"
      />
    </div>
    <div class="w-screen lg:max-w-3xl lg:max-w-md mx-auto py-m px-m">
      <AgentContactForm
        id="dialog-contact-form"
        :variant
        :property
        @update:form="submitForm($event)"
      />
      <AgentDialogLegalMention />
    </div>
    <template #actions>
      <IadBtn
        :text="$t(`agent.form.send_message`)"
        class="w-full"
        form="dialog-contact-form"
        type="submit"
        :loading
        :disabled="loading"
      />
    </template>
  </IadDialog>
</template>
